import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslations } from '../../contexts/Translations';
import { useUser } from '../../contexts/User';
import { GoogleOAuthButton } from '../GoogleOAuthButton';
import type { AuthView } from './ForgotPasswordForm';

interface LoginFormProps {
  setView: (view: AuthView) => void;
}

interface LoginFormData {
  email: string;
  password: string;
}

export default function LoginForm({ setView }: LoginFormProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const { translated } = useTranslations();
  const { login, loginWithOAuth, googleLogin } = useUser();

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .email(translated('email_invalid'))
      .required(translated('email_required')),
    password: yup.string().required(translated('password_required')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(validationSchema),
  });

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const handleLoginSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      setServerError(null);
      await login!(data.email, data.password);
    } catch (e: any) {
      setServerError(e.message || translated('login_error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async (idToken?: string) => {
    setOauthLoading(true);
    setServerError(null);
    try {
      // If idToken is provided (from native login), use googleLogin directly
      // Otherwise fall back to OAuth popup flow (web)
      if (idToken) {
        await googleLogin!(idToken);
      } else {
        await loginWithOAuth!('google');
      }
    } catch (error: any) {
      setServerError(error.message || 'Google login failed');
    } finally {
      setOauthLoading(false);
    }
  };

  return (
    <div className="mt-2">
      {/* Google OAuth Button */}
      <GoogleOAuthButton
        onSuccess={handleGoogleLogin}
        onError={(error) => setServerError(error.message)}
        loading={oauthLoading}
        className="mb-4"
      />

      {/* Divider */}
      <div className="flex items-center my-4">
        <div className="flex-1 border-t border-gray-300"></div>
        <span className="px-4 text-sm text-gray-500">
          {translated('or') || 'or'}
        </span>
        <div className="flex-1 border-t border-gray-300"></div>
      </div>

      <form
        onSubmit={handleSubmit(handleLoginSubmit)}
        className="flex flex-col items-end"
      >
        <input
          type="email"
          placeholder={translated('email')}
          {...register('email')}
          className="w-full border-2 border-gray-300 p-2 rounded-lg mb-2"
          disabled={isLoading}
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? 'email-error' : undefined}
        />
        {errors.email && (
          <p
            id="email-error"
            className="text-red-400 text-sm w-full text-left mb-2"
            role="alert"
          >
            {errors.email.message}
          </p>
        )}

        <div className="w-full relative">
          <input
            type={isPasswordVisible ? 'text' : 'password'}
            placeholder={translated('password')}
            {...register('password')}
            className="w-full border-2 border-gray-300 p-2 rounded-lg mb-2"
            disabled={isLoading}
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'password-error' : undefined}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-2 flex items-center px-2 text-gray-600"
            onClick={togglePasswordVisibility}
            aria-label={
              isPasswordVisible
                ? translated('hide_password')
                : translated('show_password')
            }
            disabled={isLoading}
          >
            {isPasswordVisible ? (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M15.0007 12C15.0007 13.6569 13.6576 15 12.0007 15C10.3439 15 9.00073 13.6569 9.00073 12C9.00073 10.3431 10.3439 9 12.0007 9C13.6576 9 15.0007 10.3431 15.0007 12Z"
                  stroke="#000000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12.0012 5C7.52354 5 3.73326 7.94288 2.45898 12C3.73324 16.0571 7.52354 19 12.0012 19C16.4788 19 20.2691 16.0571 21.5434 12C20.2691 7.94291 16.4788 5 12.0012 5Z"
                  stroke="#000000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M2.99902 3L20.999 21M9.8433 9.91364C9.32066 10.4536 8.99902 11.1892 8.99902 12C8.99902 13.6569 10.3422 15 11.999 15C12.8215 15 13.5667 14.669 14.1086 14.133M6.49902 6.64715C4.59972 7.90034 3.15305 9.78394 2.45703 12C3.73128 16.0571 7.52159 19 11.9992 19C13.9881 19 15.8414 18.4194 17.3988 17.4184M10.999 5.04939C11.328 5.01673 11.6617 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C21.2607 12.894 20.8577 13.7338 20.3522 14.5"
                  stroke="#000000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        </div>
        {errors.password && (
          <p
            id="password-error"
            className="text-red-400 text-sm w-full text-left mb-2"
            role="alert"
          >
            {errors.password.message}
          </p>
        )}

        <button
          type="button"
          className="w-full text-right text-sm text-balloon hover:underline mb-2"
          onClick={() => setView('forgotPassword')}
          disabled={isLoading}
        >
          {translated('forgot_password') || 'Forgot password?'}
        </button>

        {serverError && (
          <p
            className="text-red-400 text-sm w-full text-left mb-2"
            role="alert"
          >
            {serverError}
          </p>
        )}

        <input
          className=" bg-balloon text-white inline-flex justify-center rounded-full border border-transparent px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          type="submit"
          value={
            isLoading
              ? translated('loading') || 'Loading...'
              : translated('login')
          }
          disabled={isLoading}
        />

        <button
          type="button"
          className="mt-2 text-balloon inline-flex justify-center rounded-full border border-transparent px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          onClick={() => setView('register')}
          disabled={isLoading}
        >
          {translated('register')}
        </button>
      </form>
    </div>
  );
}
